/**
 * Calculator Data Hook
 * Handles data fetching for Calculator (services, processors, reader types, etc.)
 */

import { useState, useEffect, useCallback } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';

export const useCalculatorData = () => {
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [servicesFullData, setServicesFullData] = useState([]);
  const [processors, setProcessors] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState({ Card: [], ACH: [] });
  const [readerTypes, setReaderTypes] = useState(['Sureswipe', 'Other']);
  const [readerTypesWithCosts, setReaderTypesWithCosts] = useState([]);
  const [billingFrequencies, setBillingFrequencies] = useState(['Monthly', 'Quarterly', 'Annually']);
  const [accountExecutives, setAccountExecutives] = useState([]);

  /**
   * Load all calculator reference data
   */
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      console.log('[CalculatorData] Loading reference data...');

      const [
        servicesRes,
        processorsRes,
        paymentTypesRes,
        readerTypesRes,
        billingFreqsRes,
        accountExecsRes
      ] = await Promise.all([
        api.get('/admin/services'),  // FIXED: Changed from /services to /admin/services
        api.get('/processors'),
        api.get('/payment-types'),
        api.get('/reader-types-full'),
        api.get('/billing-frequencies'),
        api.get('/account-executives')
      ]);

      // Services
      const servicesData = servicesRes.data.services || [];
      setServices(servicesData.map(s => s.name));
      setServicesFullData(servicesData);

      // Processors
      const processorsData = processorsRes.data.processors || [];
      setProcessors(processorsData);

      // Payment Types
      const paymentTypesData = paymentTypesRes.data.payment_types || { Card: [], ACH: [] };
      // Backend returns organized object { Card: [...], ACH: [...] } not array
      const cardTypes = Array.isArray(paymentTypesData.Card) 
        ? paymentTypesData.Card.map(pt => pt.name) 
        : [];
      const achTypes = Array.isArray(paymentTypesData.ACH) 
        ? paymentTypesData.ACH.map(pt => pt.name) 
        : [];
      setPaymentTypes({ Card: cardTypes, ACH: achTypes });

      // Reader Types
      const readerTypesData = readerTypesRes.data.reader_types || [];
      const readerNames = readerTypesData.map(rt => rt.name);
      setReaderTypes(readerNames.length > 0 ? readerNames : ['Sureswipe', 'Other']);
      setReaderTypesWithCosts(readerTypesData);

      // Billing Frequencies
      const billingFreqs = billingFreqsRes.data.billing_frequencies || [];
      setBillingFrequencies(billingFreqs.length > 0 ? billingFreqs : ['Monthly', 'Quarterly', 'Annually']);

      // Account Executives
      const accountExecs = accountExecsRes.data.account_executives || [];
      // Handle both 'active' and 'is_active' flags
      const activeExecs = accountExecs.filter(ae => ae.active === true || ae.is_active === true);
      setAccountExecutives(activeExecs);

      console.log('[CalculatorData] Reference data loaded successfully');
      console.log('[CalculatorData] Services:', servicesData.length);
      console.log('[CalculatorData] Processors:', processorsData.length);
      console.log('[CalculatorData] Account Executives:', activeExecs.length);
      
    } catch (error) {
      console.error('[CalculatorData] Error loading data:', error);
      toast.error('Failed to load calculator data');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Reload data (triggered by settings changes)
   */
  const reloadData = useCallback(() => {
    console.log('[CalculatorData] Reloading reference data...');
    return loadData();
  }, [loadData]);

  /**
   * Get reader cost for selected reader type
   */
  const getReaderCost = useCallback((readerTypeName) => {
    const readerType = readerTypesWithCosts.find(rt => rt.name === readerTypeName);
    if (!readerType) {
      return { costPerUnit: 0, hasSim: false, simCost: 0 };
    }
    
    return {
      // Prefer monthly_cost (set via migration: A920=$40, A35=$40, Dynamag=$75)
      // Fall back to cost_per_unit for legacy data
      costPerUnit: readerType.monthly_cost || readerType.cost_per_unit || 0,
      hasSim: readerType.has_sim || false,
      simCost: readerType.sim_cost || 0
    };
  }, [readerTypesWithCosts]);

  // Auto-load on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Listen for reload requests from settings
  useEffect(() => {
    const shouldReload = localStorage.getItem('reload_reference_data');
    if (shouldReload) {
      console.log('[CalculatorData] Reload flag detected - reloading data');
      localStorage.removeItem('reload_reference_data');
      reloadData();
    }

    const handleStorageChange = (e) => {
      if (e.key === 'reload_reference_data') {
        console.log('[CalculatorData] Storage event detected - reloading data');
        reloadData();
        localStorage.removeItem('reload_reference_data');
      }
    };

    const handleFocus = () => {
      const shouldReload = localStorage.getItem('reload_reference_data');
      if (shouldReload) {
        console.log('[CalculatorData] Focus detected with reload flag - reloading data');
        reloadData();
        localStorage.removeItem('reload_reference_data');
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const shouldReload = localStorage.getItem('reload_reference_data');
        if (shouldReload) {
          console.log('[CalculatorData] Page became visible with reload flag - reloading data');
          reloadData();
          localStorage.removeItem('reload_reference_data');
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [reloadData]);

  return {
    // State
    loading,
    services,
    servicesFullData,
    processors,
    paymentTypes,
    readerTypes,
    readerTypesWithCosts,
    billingFrequencies,
    accountExecutives,
    
    // Actions
    loadData,
    reloadData,
    getReaderCost
  };
};
